import * as React from "react";
import {
    ContextualMenuItemType,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    FontWeights,
    Icon,
    IconButton,
    IContextualMenuItem,
    IDropdownOption,
    Label,
    mergeStyles,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    TextField,
    Toggle,
    TooltipHost,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import {
    IHixCategoryMapping,
    IHixDefaultMappingsInfo,
    IHixDocumentMapping,
    IHixMappingFieldValue,
    IHixTaskMapping,
    IHixTaskMappings,
    IHixUploadTypeOption,
    ISiteDesign,
    ISiteDesignField,
    ISiteDesignLibrary,
    SiteDesignLoadStatus,
} from "../../data-structures/interfaces";
import {
    getHixDefaultMappings,
    getTenantHixTaskMappings,
    getTenantHixUploadTypesAdmin,
    getTenantSiteDesign,
    updateHixDefaultMappings,
    updateTenantHixTaskMappings,
} from "../../utilities/helpers/ApiHelper";
import {
    HixDefaultsMergeMode,
    IHixDefaultSkip,
    IHixMappingState,
    alignTypeKeys,
    exceedsMappingsSizeLimit,
    filterDefaultsForDesign,
    findChoice,
    getUnloadedTypeKeys,
    hasCoveredContent,
    hasDefaultsContent,
    isHixIsoDate,
    mergeDefaults,
    readHixTaskMappings,
    stripCustomTaskTypes,
} from "../../utilities/helpers/hixDefaultMappings";
import {
    DOCUMENT_TYPE_BY_ROW_KEY,
    DOCUMENT_TYPE_GROUPS,
    HIX_CATEGORIES,
    KNOWN_CATEGORY_KEYS,
    STANDARD_TASK_TYPES,
    SUPPORTED_FIELD_TYPES,
    customTaskTypeKey,
    documentRowKey,
    documentTypeKeyFromRow,
    isDocumentRowKey,
    isQuestionCategoryRowKey,
    isTaskRowKey,
    isTemplateCategoryRowKey,
    mappingKey,
    questionCategoryKeyFromRow,
    questionCategoryRowKey,
    templateCategoryKeyFromRow,
    templateCategoryRowKey,
    toInternalCategoryMapping,
    toInternalDocumentMapping,
} from "../../utilities/helpers/hixMappingKeys";
import DialogMessage from "../DialogMessage";
import { MessageType } from "../../data-structures/enums";

interface HixMappingsPanelProps {
    isOpen: boolean;
    /** Tenant int db id — the backend resolves the provisioning GUID server-side. */
    tenantId: number;
    tenantName: string;
    /** Global admins may also replace the global defaults ("Save as defaults"). */
    isGlobalAdmin: boolean;
    onClose: () => void;
}

interface IMappingRow {
    /**
     * Unique rail/state key: task type key, "custom:{id}", "doc:{documentTypeKey}",
     * "tplcat:{categoryKey}" or "qstcat:{categoryKey}".
     */
    key: string;
    label: string;
    kind: "task" | "document" | "templateCategory" | "questionCategory";
    isCustom: boolean;
    hixUploadTypeId?: number;
    /** Saved custom mapping whose upload type HIX no longer returns. */
    isStale: boolean;
}

interface ITypeSection {
    key: string;
    /** The type's libraries from the site design; empty for a type only known from saved data. */
    libraries: ISiteDesignLibrary[];
}

/** What the last defaults action did, shown above the editor until the next one. */
interface IDefaultsNotice {
    mode: "auto" | HixDefaultsMergeMode;
    mappingsAdded: number;
    ignoresAdded: number;
    skipped: IHixDefaultSkip[];
}

type LanguageStrings = React.ContextType<typeof LanguageContext>;

/** Replaces "{0}", "{1}", ... in a localized template. */
const formatString = (template: string, ...args: (string | number)[]) =>
    args.reduce<string>((text, arg, index) => text.split(`{${index}}`).join(String(arg)), template);

// One line of the skip summary, e.g. "BTW-aangifte · Bedrijf › Periode: 'Jaar' is not a choice".
const describeSkip = (skip: IHixDefaultSkip, strings: LanguageStrings): string => {
    const where = [skip.rowLabel, skip.typeKey].filter(Boolean).join(" · ");
    const target = skip.fieldName ?? skip.libraryTitle;
    const prefix = target ? `${where} › ${target}` : where;
    switch (skip.reason) {
        case "unknownKey":
            return `${prefix}: ${strings.DefaultsSkipUnknownKey}`;
        case "typeNotInDesign":
            return `${prefix}: ${strings.DefaultsSkipTypeNotInDesign}`;
        case "typeNotLoaded":
            return `${prefix}: ${strings.DefaultsSkipTypeNotLoaded}`;
        case "libraryNotInType":
            return `${prefix}: ${strings.DefaultsSkipLibraryNotInType}`;
        case "fieldNotInLibrary":
            return `${prefix}: ${strings.DefaultsSkipFieldNotInLibrary}`;
        case "fieldTypeNotAllowed":
            return `${prefix}: ${strings.DefaultsSkipFieldTypeNotAllowed}`;
        case "choiceValueNotAllowed":
            return `${prefix}: '${skip.value}' ${strings.DefaultsSkipChoiceNotAllowed}`;
        case "valueNotValidForType":
            return `${prefix}: '${skip.value}' ${strings.DefaultsSkipValueNotValid}`;
        default:
            return prefix;
    }
};

const layoutStyle = mergeStyles({
    display: "flex",
    gap: 16,
    height: "calc(100vh - 210px)",
    marginTop: 10,
});

const railStyle = mergeStyles({
    borderRight: "1px solid var(--db-border)",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 4,
    overflowY: "auto",
    paddingRight: 12,
    width: 320,
});

const railGroupHeaderStyle = mergeStyles({
    color: "var(--db-fg)",
    fontSize: 12,
    fontWeight: FontWeights.semibold,
    marginTop: 10,
    opacity: 0.8,
    textTransform: "uppercase",
});

/** Divider header separating the task-type rows from the document-type rows. */
const railSectionHeaderStyle = mergeStyles(railGroupHeaderStyle, {
    borderTop: "1px solid var(--db-border)",
    marginTop: 18,
    paddingTop: 14,
});

const railRowStyle = mergeStyles({
    alignItems: "center",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    gap: 8,
    justifyContent: "space-between",
    padding: "8px 10px",
    textAlign: "left",
    width: "100%",
    selectors: {
        ":hover": { backgroundColor: "var(--db-surface)" },
    },
});

const railRowSelectedStyle = mergeStyles({
    backgroundColor: "var(--db-surface-raised)",
    border: "1px solid var(--db-border)",
});

const railRowLabelStyle = mergeStyles({
    color: "var(--db-fg)",
    fontSize: 13,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const pillBaseStyle = mergeStyles({
    alignItems: "center",
    borderRadius: 8,
    display: "inline-flex",
    flexShrink: 0,
    fontSize: 11,
    fontWeight: FontWeights.semibold,
    justifyContent: "center",
    maxWidth: 130,
    overflow: "hidden",
    padding: "2px 8px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const mappedPillStyle = mergeStyles(pillBaseStyle, {
    backgroundColor: "var(--db-pill-info-bg)",
    border: "1px solid var(--db-pill-info-border)",
    color: "var(--db-pill-info-fg)",
});

const unmappedPillStyle = mergeStyles(pillBaseStyle, {
    backgroundColor: "var(--db-pill-neutral-bg)",
    border: "1px solid var(--db-pill-neutral-border)",
    color: "var(--db-pill-neutral-fg)",
});

const detailPaneStyle = mergeStyles({
    flexGrow: 1,
    minWidth: 0,
    overflowY: "auto",
    paddingRight: 16,
});

const typeSectionStyle = mergeStyles({
    backgroundColor: "var(--db-surface-raised)",
    border: "1px solid var(--db-border)",
    borderRadius: 8,
    boxSizing: "border-box",
    padding: "12px 16px 16px 16px",
});

const warningIconStyle = mergeStyles({
    color: "var(--db-pill-warning-fg)",
    flexShrink: 0,
});

const HixMappingsPanelContent: React.FunctionComponent<HixMappingsPanelProps> = ({
    isOpen,
    tenantId,
    tenantName,
    isGlobalAdmin,
    onClose,
}) => {
    const languageStrings = React.useContext(LanguageContext);

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [siteDesignStatus, setSiteDesignStatus] = React.useState<SiteDesignLoadStatus>("ok");
    const [siteDesign, setSiteDesign] = React.useState<ISiteDesign | undefined>(undefined);
    const [hixConfigured, setHixConfigured] = React.useState<boolean>(false);
    const [customUploadTypes, setCustomUploadTypes] = React.useState<IHixUploadTypeOption[]>([]);
    const [loadedMappings, setLoadedMappings] = React.useState<IHixTaskMapping[]>([]);
    const [loadedDocumentMappings, setLoadedDocumentMappings] = React.useState<IHixDocumentMapping[]>([]);
    const [loadedTemplateCategoryMappings, setLoadedTemplateCategoryMappings] = React.useState<IHixCategoryMapping[]>([]);
    const [loadedQuestionCategoryMappings, setLoadedQuestionCategoryMappings] = React.useState<IHixCategoryMapping[]>([]);
    /** Ignored keys as merged rail keys: task keys verbatim, document/category keys prefixed. */
    const [loadedIgnored, setLoadedIgnored] = React.useState<string[]>([]);
    const [mappingsLoadFailed, setMappingsLoadFailed] = React.useState<boolean>(false);
    const [mappingByKey, setMappingByKey] = React.useState<Record<string, IHixTaskMapping>>({});
    const [ignoredKeys, setIgnoredKeys] = React.useState<Set<string>>(new Set());
    const [selectedKey, setSelectedKey] = React.useState<string>(STANDARD_TASK_TYPES[0].key);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailed, setSaveFailed] = React.useState<boolean>(false);
    const [showCloseConfirm, setShowCloseConfirm] = React.useState<boolean>(false);
    /** Source typeKey of a pending "Copy to other types" awaiting confirmation. */
    const [copyConfirmSource, setCopyConfirmSource] = React.useState<string | null>(null);
    const [defaultsInfo, setDefaultsInfo] = React.useState<IHixDefaultMappingsInfo | undefined>(undefined);
    const [defaultsLoadFailed, setDefaultsLoadFailed] = React.useState<boolean>(false);
    /** The tenant had no saved mappings at all when the panel opened. */
    const [neverConfigured, setNeverConfigured] = React.useState<boolean>(false);
    const [defaultsNotice, setDefaultsNotice] = React.useState<IDefaultsNotice | null>(null);
    const [showReplaceConfirm, setShowReplaceConfirm] = React.useState<boolean>(false);
    const [showSaveDefaultsConfirm, setShowSaveDefaultsConfirm] = React.useState<boolean>(false);
    const [isSavingDefaults, setIsSavingDefaults] = React.useState<boolean>(false);
    const [defaultsSaveResult, setDefaultsSaveResult] = React.useState<"saved" | "failed" | "tooLarge" | null>(null);
    /** Malformed parts of the tenant's saved blob that were left out on load. */
    const [unreadableSaved, setUnreadableSaved] = React.useState<number>(0);
    /** Auto-fill was held back because a type the defaults use did not load. */
    const [autoFillDeferred, setAutoFillDeferred] = React.useState<boolean>(false);
    const [saveTooLarge, setSaveTooLarge] = React.useState<boolean>(false);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [designResult, uploadTypesResult, mappingsResult] = await Promise.all([
                    getTenantSiteDesign(tenantId),
                    getTenantHixUploadTypesAdmin(tenantId),
                    getTenantHixTaskMappings(tenantId),
                ]);
                // Defaults are kept per template configuration (spec 2026-10-09 §3.4): they are read
                // for the configuration the site design names, so they wait for it. Without a key
                // (an older API) they count as not loaded.
                const designConfigurationKey = designResult.status === "ok" ? designResult.design?.configurationKey?.trim() : undefined;
                const defaultsResult = designConfigurationKey ? await getHixDefaultMappings(designConfigurationKey) : undefined;
                if (cancelled) {
                    return;
                }

                setSiteDesignStatus(designResult.status);
                setSiteDesign(designResult.design);
                const design = designResult.status === "ok" ? designResult.design : undefined;

                setHixConfigured(uploadTypesResult?.hixConfigured === true);
                // Dedupe defensively by id and case-insensitive name.
                const seenIds = new Set<number>();
                const seenNames = new Set<string>();
                const uniqueTypes = (uploadTypesResult?.uploadTypes ?? []).filter((t) => {
                    const nameKey = t.name?.trim()?.toLowerCase() ?? "";
                    if (!t.id || !nameKey || seenIds.has(t.id) || seenNames.has(nameKey)) {
                        return false;
                    }
                    seenIds.add(t.id);
                    seenNames.add(nameKey);
                    return true;
                });
                setCustomUploadTypes(uniqueTypes);

                // The tenant's blob, read defensively: malformed parts are left out (and counted) so
                // they can never reach the state or the rendering. undefined = failed, null = never
                // configured.
                const tenantRead = mappingsResult ? readHixTaskMappings(mappingsResult) : undefined;
                const tenant = mappingsResult === undefined || (mappingsResult && !tenantRead) ? undefined : tenantRead?.mappings ?? null;
                setUnreadableSaved(tenantRead?.unreadable ?? 0);

                // Entries without a typeKey are from the pre-per-type dev shape — dropped on load
                // (the feature is unreleased; no migration). Type keys that differ from the design
                // only in case are rewritten to the design's spelling, so they show and survive Save.
                const savedMappings = alignTypeKeys(
                    (tenant === undefined ? [] : tenant?.mappings ?? []).filter((m) => m?.taskTypeKey && m?.typeKey),
                    (m) => m.taskTypeKey,
                    design
                );
                setMappingsLoadFailed(tenant === undefined);
                setLoadedMappings(savedMappings);
                const savedDocumentMappings = alignTypeKeys(
                    (tenant === undefined ? [] : tenant?.documentMappings ?? []).filter((m) => m?.documentTypeKey && m?.typeKey),
                    (m) => documentRowKey(m.documentTypeKey),
                    design
                );
                setLoadedDocumentMappings(savedDocumentMappings);
                const savedTemplateCategoryMappings = alignTypeKeys(
                    (tenant === undefined ? [] : tenant?.templateCategoryMappings ?? []).filter((m) => m?.categoryKey && m?.typeKey),
                    (m) => templateCategoryRowKey(m.categoryKey),
                    design
                );
                setLoadedTemplateCategoryMappings(savedTemplateCategoryMappings);
                const savedQuestionCategoryMappings = alignTypeKeys(
                    (tenant === undefined ? [] : tenant?.questionCategoryMappings ?? []).filter((m) => m?.categoryKey && m?.typeKey),
                    (m) => questionCategoryRowKey(m.categoryKey),
                    design
                );
                setLoadedQuestionCategoryMappings(savedQuestionCategoryMappings);
                const record: Record<string, IHixTaskMapping> = {};
                savedMappings.forEach((m) => {
                    record[mappingKey(m.taskTypeKey, m.typeKey)] = { ...m, fields: m.fields ?? [] };
                });
                savedDocumentMappings.forEach((m) => {
                    record[mappingKey(documentRowKey(m.documentTypeKey), m.typeKey)] = toInternalDocumentMapping(m);
                });
                savedTemplateCategoryMappings.forEach((m) => {
                    record[mappingKey(templateCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, templateCategoryRowKey);
                });
                savedQuestionCategoryMappings.forEach((m) => {
                    record[mappingKey(questionCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, questionCategoryRowKey);
                });
                const prefixIgnored = (keys: unknown[] | undefined, rowKey: (k: string) => string) =>
                    (tenant === undefined ? [] : keys ?? []).map((k) => (typeof k === "string" ? rowKey(k) : k));
                const savedIgnored = Array.from(
                    new Set(
                        [
                            ...(tenant === undefined ? [] : tenant?.ignoredTaskTypes ?? []),
                            ...prefixIgnored(tenant?.ignoredDocumentTypes, documentRowKey),
                            ...prefixIgnored(tenant?.ignoredTemplateCategories, templateCategoryRowKey),
                            ...prefixIgnored(tenant?.ignoredQuestionCategories, questionCategoryRowKey),
                        ].filter((k): k is string => typeof k === "string" && !!k)
                    )
                );
                setLoadedIgnored(savedIgnored);

                // The API helper already rejects malformed defaults; they are read again here so a bad
                // blob can never reach the auto-fill below.
                const defaultsRead = defaultsResult?.mappings ? readHixTaskMappings(defaultsResult.mappings, { strict: true }) : undefined;
                const defaultsUsable = defaultsResult !== undefined && (!defaultsResult.mappings || defaultsRead?.unreadable === 0);
                setDefaultsInfo(defaultsUsable ? defaultsResult : undefined);
                setDefaultsLoadFailed(!defaultsUsable);
                setNeverConfigured(tenant === null);

                // Auto-fill (spec §4.2): a tenant that has never saved mappings starts from the filtered
                // defaults. Only the working state changes; the baseline stays empty, so Save is armed and
                // nothing is written until the admin clicks it.
                let filled: IHixMappingState | undefined;
                if (tenant === null && design && defaultsUsable && defaultsResult?.mappings) {
                    const filtered = filterDefaultsForDesign(defaultsResult.mappings, design);
                    if (filtered.skipped.some((s) => s.reason === "typeNotLoaded")) {
                        // Part of the design failed to load: filling now would arm Save with an incomplete
                        // set, and once saved the tenant is never auto-filled again. A later open retries.
                        setAutoFillDeferred(true);
                    } else {
                        const merged = mergeDefaults({ record: {}, ignored: new Set() }, filtered, "replace");
                        filled = merged.state;
                        setDefaultsNotice({
                            mode: "auto",
                            mappingsAdded: merged.mappingsAdded,
                            ignoresAdded: merged.ignoresAdded,
                            skipped: filtered.skipped,
                        });
                    }
                }
                setMappingByKey(filled ? filled.record : record);
                setIgnoredKeys(filled ? filled.ignored : new Set(savedIgnored));
                setIsLoading(false);
            } catch (error) {
                // Whatever failed, never leave the spinner running: the panel opens read-only.
                console.error("HixMappingsPanel: loading failed", error);
                if (!cancelled) {
                    setMappingsLoadFailed(true);
                    setIsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    // Row model: standard task types first (fixed order), then live custom types, then saved
    // custom mappings whose upload type HIX no longer returns (never silently dropped), then
    // the fixed document types, then saved document mappings whose type key this build does not
    // know (saved by newer code — also never silently dropped).
    const rows = React.useMemo<IMappingRow[]>(() => {
        const standardRows: IMappingRow[] = STANDARD_TASK_TYPES.map((t) => ({
            key: t.key,
            label: t.label,
            kind: "task",
            isCustom: false,
            isStale: false,
        }));
        const customRows: IMappingRow[] = customUploadTypes.map((t) => ({
            key: customTaskTypeKey(t.id),
            label: t.name,
            kind: "task",
            isCustom: true,
            hixUploadTypeId: t.id,
            isStale: false,
        }));
        const knownKeys = new Set<string>([...standardRows, ...customRows].map((r) => r.key));
        const staleByKey = new Map<string, IMappingRow>();
        loadedMappings
            .filter((m) => m.isCustom && !knownKeys.has(m.taskTypeKey))
            .forEach((m) => {
                if (!staleByKey.has(m.taskTypeKey)) {
                    staleByKey.set(m.taskTypeKey, {
                        key: m.taskTypeKey,
                        label: m.taskTypeLabel ?? m.taskTypeKey,
                        kind: "task",
                        isCustom: true,
                        hixUploadTypeId: m.hixUploadTypeId,
                        // Only flag "no longer in HIX" when we actually got the live list.
                        isStale: hixConfigured,
                    });
                }
            });
        const documentRows: IMappingRow[] = DOCUMENT_TYPE_GROUPS.flatMap((g) => g.types).map((t) => ({
            key: documentRowKey(t.key),
            label: t.label,
            kind: "document",
            isCustom: false,
            isStale: false,
        }));
        const unknownDocRows: IMappingRow[] = [];
        loadedDocumentMappings
            .filter((m) => !DOCUMENT_TYPE_BY_ROW_KEY.has(documentRowKey(m.documentTypeKey)))
            .forEach((m) => {
                const key = documentRowKey(m.documentTypeKey);
                if (!unknownDocRows.some((r) => r.key === key)) {
                    unknownDocRows.push({
                        key,
                        label: m.documentTypeKey,
                        kind: "document",
                        isCustom: false,
                        isStale: false,
                    });
                }
            });
        // The fixed categories, then saved category mappings whose key this build does not know
        // (saved by newer code — never silently dropped). Template and question categories share
        // the same fixed set but are independent rows.
        const categoryRows = (
            kind: "templateCategory" | "questionCategory",
            rowKey: (categoryKey: string) => string,
            loaded: IHixCategoryMapping[]
        ): IMappingRow[] => {
            const known: IMappingRow[] = HIX_CATEGORIES.map((c) => ({
                key: rowKey(c.key),
                label: c.label,
                kind,
                isCustom: false,
                isStale: false,
            }));
            loaded
                .filter((m) => !KNOWN_CATEGORY_KEYS.has(m.categoryKey))
                .forEach((m) => {
                    const key = rowKey(m.categoryKey);
                    if (!known.some((r) => r.key === key)) {
                        known.push({ key, label: m.categoryKey, kind, isCustom: false, isStale: false });
                    }
                });
            return known;
        };
        return [
            ...standardRows,
            ...customRows,
            ...Array.from(staleByKey.values()),
            ...documentRows,
            ...unknownDocRows,
            ...categoryRows("templateCategory", templateCategoryRowKey, loadedTemplateCategoryMappings),
            ...categoryRows("questionCategory", questionCategoryRowKey, loadedQuestionCategoryMappings),
        ];
    }, [
        customUploadTypes,
        loadedMappings,
        loadedDocumentMappings,
        loadedTemplateCategoryMappings,
        loadedQuestionCategoryMappings,
        hixConfigured,
    ]);

    // One section per site-design type (Bedrijf / Persoon / Overig ...), in sortOrder; types
    // that only exist in saved mappings (design changed since save) are appended with no
    // libraries so the saved data stays visible instead of vanishing.
    const typeSections = React.useMemo<ITypeSection[]>(() => {
        const sections: ITypeSection[] = [...(siteDesign?.types ?? [])]
            .filter((t) => t?.key)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((t) => ({ key: t.key, libraries: t.libraries ?? [] }));
        const knownTypeKeys = new Set(sections.map((s) => s.key.toLowerCase()));
        [
            ...loadedMappings,
            ...loadedDocumentMappings,
            ...loadedTemplateCategoryMappings,
            ...loadedQuestionCategoryMappings,
        ].forEach((m) => {
            if (m.typeKey && !knownTypeKeys.has(m.typeKey.toLowerCase())) {
                knownTypeKeys.add(m.typeKey.toLowerCase());
                sections.push({ key: m.typeKey, libraries: [] });
            }
        });
        return sections;
    }, [siteDesign, loadedMappings, loadedDocumentMappings, loadedTemplateCategoryMappings, loadedQuestionCategoryMappings]);

    const canEdit = siteDesignStatus === "ok" && !mappingsLoadFailed;
    // The template configuration this tenant's defaults belong to (spec 2026-10-09 §3.4).
    const configurationKey = siteDesign?.configurationKey?.trim() || undefined;
    const configurationName = siteDesign?.displayName?.trim() || configurationKey || "";

    // Canonical serialization: mapped entries only, in row order then type order, fields
    // sorted by internalName, ignored keys sorted — so reordering-only state churn never
    // arms Save. Document entries are canonicalized in their clean saved shape (no labels,
    // no custom/upload-type fields) so load-vs-edit label differences never arm Save either.
    const canonicalize = React.useCallback(
        (record: Record<string, IHixTaskMapping>, ignored: ReadonlySet<string>): string => {
            const canonicalFields = (m: IHixTaskMapping) =>
                [...(m.fields ?? [])]
                    .filter((f) => f.internalName)
                    .sort((a, b) => a.internalName.localeCompare(b.internalName))
                    .map((f) => ({ internalName: f.internalName, value: f.value ?? "" }));
            const mapped = (kind: IMappingRow["kind"]) =>
                rows
                    .filter((row) => row.kind === kind)
                    .flatMap((row) => typeSections.map((section) => record[mappingKey(row.key, section.key)]))
                    .filter((m) => m?.libraryTitle);
            return JSON.stringify({
                ignored: Array.from(ignored).sort(),
                mappings: mapped("task").map((m) => ({
                    taskTypeKey: m.taskTypeKey,
                    taskTypeLabel: m.taskTypeLabel,
                    isCustom: m.isCustom,
                    hixUploadTypeId: m.hixUploadTypeId,
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                documentMappings: mapped("document").map((m) => ({
                    documentTypeKey: documentTypeKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                templateCategoryMappings: mapped("templateCategory").map((m) => ({
                    categoryKey: templateCategoryKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
                questionCategoryMappings: mapped("questionCategory").map((m) => ({
                    categoryKey: questionCategoryKeyFromRow(m.taskTypeKey),
                    typeKey: m.typeKey,
                    libraryTitle: m.libraryTitle,
                    fields: canonicalFields(m),
                })),
            });
        },
        [rows, typeSections]
    );

    const initialCanonical = React.useMemo(() => {
        const record: Record<string, IHixTaskMapping> = {};
        loadedMappings.forEach((m) => {
            record[mappingKey(m.taskTypeKey, m.typeKey)] = m;
        });
        loadedDocumentMappings.forEach((m) => {
            record[mappingKey(documentRowKey(m.documentTypeKey), m.typeKey)] = toInternalDocumentMapping(m);
        });
        loadedTemplateCategoryMappings.forEach((m) => {
            record[mappingKey(templateCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, templateCategoryRowKey);
        });
        loadedQuestionCategoryMappings.forEach((m) => {
            record[mappingKey(questionCategoryRowKey(m.categoryKey), m.typeKey)] = toInternalCategoryMapping(m, questionCategoryRowKey);
        });
        return canonicalize(record, new Set(loadedIgnored));
    }, [
        loadedMappings,
        loadedDocumentMappings,
        loadedTemplateCategoryMappings,
        loadedQuestionCategoryMappings,
        loadedIgnored,
        canonicalize,
    ]);

    const isDirty = !isLoading && canonicalize(mappingByKey, ignoredKeys) !== initialCanonical;

    const selectedRow = rows.find((r) => r.key === selectedKey) ?? rows[0];

    const isSelectedIgnored = selectedRow ? ignoredKeys.has(selectedRow.key) : false;
    // Ignored task/document types keep their mappings (so un-ignoring restores them) but are
    // not editable.
    const canEditSelected = canEdit && !isSelectedIgnored;
    const selectedDocDef = selectedRow?.kind === "document" ? DOCUMENT_TYPE_BY_ROW_KEY.get(selectedRow.key) : undefined;

    const toggleIgnored = (rowKey: string, ignored: boolean) => {
        setIgnoredKeys((prev) => {
            const next = new Set(prev);
            if (ignored) {
                next.add(rowKey);
            } else {
                next.delete(rowKey);
            }
            return next;
        });
    };

    const getMapping = (typeKey: string): IHixTaskMapping | undefined =>
        selectedRow ? mappingByKey[mappingKey(selectedRow.key, typeKey)] : undefined;

    const updateMapping = (typeKey: string, updater: (current: IHixTaskMapping | undefined) => IHixTaskMapping | undefined) => {
        if (!selectedRow) {
            return;
        }
        const key = mappingKey(selectedRow.key, typeKey);
        setMappingByKey((prev) => {
            const next = { ...prev };
            const updated = updater(prev[key]);
            if (updated) {
                next[key] = updated;
            } else {
                delete next[key];
            }
            return next;
        });
    };

    const onLibraryChange = (typeKey: string, option?: IDropdownOption) => {
        const libraryTitle = (option?.key as string) ?? "";
        updateMapping(typeKey, () => {
            if (!libraryTitle) {
                // "Unmapped" clears the whole entry, including its field values.
                return undefined;
            }
            return {
                taskTypeKey: selectedRow.key,
                taskTypeLabel: selectedRow.label,
                isCustom: selectedRow.isCustom,
                hixUploadTypeId: selectedRow.hixUploadTypeId,
                typeKey,
                libraryTitle,
                fields: [],
            };
        });
    };

    const updateField = (typeKey: string, index: number, patch: Partial<IHixMappingFieldValue>) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            const fields = current.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
            return { ...current, fields };
        });
    };

    const removeField = (typeKey: string, index: number) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            return { ...current, fields: current.fields.filter((_, i) => i !== index) };
        });
    };

    const addField = (typeKey: string) => {
        updateMapping(typeKey, (current) => {
            if (!current) {
                return current;
            }
            return { ...current, fields: [...current.fields, { internalName: "", value: "" }] };
        });
    };

    // Per-target preview for the copy confirmation dialog: what will be set, what an existing
    // mapping being overwritten looks like, and what cannot travel.
    const buildCopyPreview = (sourceTypeKey: string) => {
        const source = getMapping(sourceTypeKey);
        if (!source?.libraryTitle || !selectedRow) {
            return [];
        }
        const sourceFields = (source.fields ?? []).filter((f) => f.internalName);
        return typeSections
            .filter((section) => section.key !== sourceTypeKey)
            .map((section) => {
                const targetLibrary = section.libraries.find(
                    (l) => l.title.toLowerCase() === source.libraryTitle.toLowerCase()
                );
                if (!targetLibrary) {
                    return { typeKey: section.key, willSet: false, text: languageStrings.CopyNotAvailable };
                }
                const targetFieldNames = new Set(targetLibrary.fields.map((f) => f.internalName));
                const copyableCount = sourceFields.filter((f) => targetFieldNames.has(f.internalName)).length;
                const skippedCount = sourceFields.length - copyableCount;
                const existing = mappingByKey[mappingKey(selectedRow.key, section.key)];
                let text = `"${targetLibrary.title}" (${copyableCount} ${languageStrings.FieldsSuffix})`;
                if (existing?.libraryTitle) {
                    text += ` — ${languageStrings.CopyOverwritesExisting} ("${existing.libraryTitle}")`;
                }
                if (skippedCount > 0) {
                    text += ` — ${skippedCount} ${languageStrings.CopyFieldsSkipped}`;
                }
                return { typeKey: section.key, willSet: true, text };
            });
    };

    // Copies a section's mapping to every OTHER type that has a library with the same title;
    // only fields that exist (by internalName) in the target type's library travel with it.
    const copyToOtherTypes = (sourceTypeKey: string) => {
        const source = getMapping(sourceTypeKey);
        if (!source?.libraryTitle || !selectedRow) {
            return;
        }
        setMappingByKey((prev) => {
            const next = { ...prev };
            typeSections
                .filter((section) => section.key !== sourceTypeKey)
                .forEach((section) => {
                    const targetLibrary = section.libraries.find(
                        (l) => l.title.toLowerCase() === source.libraryTitle.toLowerCase()
                    );
                    if (!targetLibrary) {
                        return;
                    }
                    const targetFieldNames = new Set(targetLibrary.fields.map((f) => f.internalName));
                    next[mappingKey(selectedRow.key, section.key)] = {
                        taskTypeKey: selectedRow.key,
                        taskTypeLabel: selectedRow.label,
                        isCustom: selectedRow.isCustom,
                        hixUploadTypeId: selectedRow.hixUploadTypeId,
                        typeKey: section.key,
                        libraryTitle: targetLibrary.title,
                        fields: (source.fields ?? [])
                            .filter((f) => f.internalName && targetFieldNames.has(f.internalName))
                            .map((f) => ({ ...f })),
                    };
                });
            return next;
        });
    };

    // The exact payload Save sends; "Save as defaults" sends the same minus custom task types.
    const buildMappingsPayload = (): IHixTaskMappings => {
        const mappedEntries = (kind: IMappingRow["kind"]) =>
            rows
                .filter((row) => row.kind === kind)
                .flatMap((row) => typeSections.map((section) => mappingByKey[mappingKey(row.key, section.key)]))
                .filter((m) => m?.libraryTitle);
        return {
            version: 1,
            updatedAtUtc: new Date().toISOString(),
            mappings: mappedEntries("task").map((m) => ({
                ...m,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredTaskTypes: Array.from(ignoredKeys).filter(isTaskRowKey).sort(),
            documentMappings: mappedEntries("document").map((m) => ({
                documentTypeKey: documentTypeKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredDocumentTypes: Array.from(ignoredKeys)
                .filter(isDocumentRowKey)
                .map(documentTypeKeyFromRow)
                .sort(),
            templateCategoryMappings: mappedEntries("templateCategory").map((m) => ({
                categoryKey: templateCategoryKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredTemplateCategories: Array.from(ignoredKeys)
                .filter(isTemplateCategoryRowKey)
                .map(templateCategoryKeyFromRow)
                .sort(),
            questionCategoryMappings: mappedEntries("questionCategory").map((m) => ({
                categoryKey: questionCategoryKeyFromRow(m.taskTypeKey),
                typeKey: m.typeKey,
                libraryTitle: m.libraryTitle,
                fields: (m.fields ?? []).filter((f) => f.internalName),
            })),
            ignoredQuestionCategories: Array.from(ignoredKeys)
                .filter(isQuestionCategoryRowKey)
                .map(questionCategoryKeyFromRow)
                .sort(),
        };
    };

    const onSave = async () => {
        const payload = buildMappingsPayload();
        // Checked before sending: the API would reject it, and retrying cannot help.
        const tooLarge = exceedsMappingsSizeLimit(payload);
        setSaveTooLarge(tooLarge);
        if (tooLarge) {
            setSaveFailed(false);
            return;
        }
        setIsSaving(true);
        setSaveFailed(false);
        const success = await updateTenantHixTaskMappings(tenantId, payload);
        setIsSaving(false);
        if (success) {
            onClose();
        } else {
            setSaveFailed(true);
        }
    };

    // Fill gaps / Replace with defaults (spec §4.2): filter for this tenant's design, merge into the
    // working state only, and report what happened. Saving stays the admin's explicit choice.
    const applyDefaults = (mode: HixDefaultsMergeMode) => {
        if (!defaultsInfo?.mappings || !siteDesign) {
            return;
        }
        const filtered = filterDefaultsForDesign(defaultsInfo.mappings, siteDesign);
        // Types the design failed to load keep their saved entries (the panel shows them read-only).
        const merged = mergeDefaults({ record: mappingByKey, ignored: ignoredKeys }, filtered, mode, {
            unloadedTypeKeys: getUnloadedTypeKeys(siteDesign),
        });
        setMappingByKey(merged.state.record);
        setIgnoredKeys(merged.state.ignored);
        setDefaultsNotice({ mode, mappingsAdded: merged.mappingsAdded, ignoresAdded: merged.ignoresAdded, skipped: filtered.skipped });
    };

    // Global admins replace this configuration's defaults with what this panel shows (spec
    // 2026-10-09 §3.4). The tenant's own Save state is untouched; the in-memory defaults follow
    // the saved value so Fill/Replace immediately use it.
    const saveAsDefaults = async () => {
        setShowSaveDefaultsConfirm(false);
        if (!configurationKey) {
            return;
        }
        setIsSavingDefaults(true);
        setDefaultsSaveResult(null);
        const payload = stripCustomTaskTypes(buildMappingsPayload());
        const saved = await updateHixDefaultMappings(configurationKey, payload);
        setIsSavingDefaults(false);
        if (saved) {
            setDefaultsInfo({ configurationKey, mappings: payload, updatedAtUtc: saved.updatedAtUtc, updatedBy: saved.updatedBy });
            setDefaultsLoadFailed(false);
            setDefaultsSaveResult("saved");
        } else {
            setDefaultsSaveResult("failed");
        }
    };

    // What "Save as defaults" would send, built only for global admins (the only ones offered it).
    // Never an empty set: registration copies the defaults into every new tenant, and a tenant
    // holding an empty set is never auto-filled again.
    const defaultsPayload = isGlobalAdmin ? stripCustomTaskTypes(buildMappingsPayload()) : undefined;
    const canSaveAsDefaults = canEdit && !!configurationKey && !!defaultsPayload && hasDefaultsContent(defaultsPayload);

    const canApplyDefaults = canEdit && !!siteDesign && !!defaultsInfo?.mappings;
    const defaultsUnavailableReason = !canEdit
        ? undefined
        : defaultsLoadFailed
            ? languageStrings.DefaultsLoadFailed
            : !defaultsInfo?.mappings
                ? languageStrings.NoDefaultsSaved
                : undefined;

    const defaultsMenuItems: IContextualMenuItem[] = [
        {
            key: "fillDefaults",
            text: languageStrings.FillGapsFromDefaults,
            disabled: !canApplyDefaults,
            title: defaultsUnavailableReason,
            onClick: () => applyDefaults("fill"),
        },
        {
            key: "replaceDefaults",
            text: languageStrings.ReplaceWithDefaults,
            disabled: !canApplyDefaults,
            title: defaultsUnavailableReason,
            onClick: () => {
                if (hasCoveredContent({ record: mappingByKey, ignored: ignoredKeys }, { unloadedTypeKeys: getUnloadedTypeKeys(siteDesign) })) {
                    setShowReplaceConfirm(true);
                } else {
                    applyDefaults("replace");
                }
            },
        },
        ...(isGlobalAdmin
            ? [
                { key: "defaultsDivider", itemType: ContextualMenuItemType.Divider },
                {
                    key: "saveAsDefaults",
                    text: languageStrings.SaveAsDefaults,
                    disabled: !canSaveAsDefaults || isSavingDefaults,
                    // When the panel cannot be edited the status bars already say why.
                    title: !canEdit
                        ? undefined
                        : !configurationKey
                            ? languageStrings.SaveAsDefaultsNoConfiguration
                            : !canSaveAsDefaults
                                ? languageStrings.NothingToSaveAsDefaults
                                : undefined,
                    onClick: () => {
                        // Checked before asking: the API would reject it and retrying cannot help.
                        const tooLarge = !!defaultsPayload && exceedsMappingsSizeLimit(defaultsPayload);
                        setDefaultsSaveResult(tooLarge ? "tooLarge" : null);
                        setShowSaveDefaultsConfirm(!tooLarge);
                    },
                },
            ]
            : []),
    ];

    const attemptClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            onClose();
        }
    };

    const buildLibraryOptions = (section: ITypeSection, mapping?: IHixTaskMapping): IDropdownOption[] => {
        const options: IDropdownOption[] = [{ key: "", text: languageStrings.Unmapped }];
        section.libraries.forEach((library) => {
            options.push({
                key: library.title,
                text: library.isDeprecated ? `${library.title} ${languageStrings.DeprecatedSuffix}` : library.title,
            });
        });
        // Keep a stale saved library selectable so the admin can see (and fix) it.
        if (
            mapping?.libraryTitle &&
            !section.libraries.some((l) => l.title.toLowerCase() === mapping.libraryTitle.toLowerCase())
        ) {
            options.push({
                key: mapping.libraryTitle,
                text: `${mapping.libraryTitle} — ${languageStrings.NoLongerInSiteDesign}`,
            });
        }
        return options;
    };

    const renderValueEditor = (
        typeKey: string,
        fieldValue: IHixMappingFieldValue,
        index: number,
        fieldDef?: ISiteDesignField
    ) => {
        const onValueChange = (value: string) => updateField(typeKey, index, { value });
        if (!fieldDef) {
            // Saved field that no longer exists in this type's library: editable as raw text
            // so the admin can still see the value, flagged with a warning next to the row.
            return <TextField value={fieldValue.value} disabled={!canEditSelected} onChange={(_, v) => onValueChange(v ?? "")} />;
        }
        switch (fieldDef.fieldType) {
            case "Choice":
                return (
                    <Dropdown
                        options={(fieldDef.choices ?? []).map((c) => ({ key: c, text: c }))}
                        // Display only: a saved "Inkomstenbelasting" shows as the site's "Inkomsten
                        // belasting", which delivery writes (spec 2026-10-09 §3.4); the value is not
                        // rewritten, so opening the panel never arms Save.
                        selectedKey={findChoice(fieldDef.choices, fieldValue.value) ?? (fieldValue.value || undefined)}
                        disabled={!canEditSelected}
                        onChange={(_, option) => onValueChange((option?.key as string) ?? "")}
                    />
                );
            case "Boolean":
                return (
                    <Toggle
                        checked={fieldValue.value === "true"}
                        disabled={!canEditSelected}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        styles={{ root: { marginBottom: 0 } }}
                        onChange={(_, checked) => onValueChange(checked ? "true" : "false")}
                    />
                );
            case "Number":
            case "Integer":
            case "Currency":
                return (
                    <TextField
                        type="number"
                        value={fieldValue.value}
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            case "Note":
                return (
                    <TextField
                        multiline
                        rows={2}
                        value={fieldValue.value}
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            case "DateTime":
                return (
                    <TextField
                        placeholder="2026-01-31T00:00:00Z"
                        value={fieldValue.value}
                        errorMessage={
                            fieldValue.value.trim() !== "" && !isHixIsoDate(fieldValue.value)
                                ? languageStrings.InvalidHixDate
                                : undefined
                        }
                        disabled={!canEditSelected}
                        onChange={(_, v) => onValueChange(v ?? "")}
                    />
                );
            default:
                return <TextField value={fieldValue.value} disabled={!canEditSelected} onChange={(_, v) => onValueChange(v ?? "")} />;
        }
    };

    const renderFieldRow = (
        section: ITypeSection,
        mapping: IHixTaskMapping,
        chosenLibrary: ISiteDesignLibrary | undefined,
        fieldValue: IHixMappingFieldValue,
        index: number
    ) => {
        const libraryFields = chosenLibrary?.fields ?? [];
        const selectableFields = libraryFields.filter((f) => SUPPORTED_FIELD_TYPES.includes(f.fieldType));
        const fieldDef = selectableFields.find((f) => f.internalName === fieldValue.internalName);
        // Spec 2026-10-09 §3.4: a column that exists but has a type delivery does not write.
        const isUnsupportedField =
            !!fieldValue.internalName && !fieldDef && libraryFields.some((f) => f.internalName === fieldValue.internalName);
        const isStaleField = !!fieldValue.internalName && !!chosenLibrary && !fieldDef && !isUnsupportedField;
        const fieldWarning = isUnsupportedField
            ? languageStrings.FieldTypeNotSupported
            : isStaleField
                ? languageStrings.NoLongerInSiteDesign
                : undefined;
        const assignedNames = new Set(mapping.fields.map((f) => f.internalName));
        const fieldOptions: IDropdownOption[] = selectableFields
            .filter((f) => f.internalName === fieldValue.internalName || !assignedNames.has(f.internalName))
            .map((f) => ({
                key: f.internalName,
                // "*" marks fields the library schema requires.
                text: `${f.displayName || f.internalName}${f.required ? " *" : ""}`,
            }));
        if (fieldWarning) {
            fieldOptions.push({
                key: fieldValue.internalName,
                text: `${fieldValue.internalName} — ${fieldWarning}`,
            });
        }
        return (
            <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="start">
                <Stack.Item styles={{ root: { width: 300 } }}>
                    <Dropdown
                        options={fieldOptions}
                        selectedKey={fieldValue.internalName || undefined}
                        placeholder={languageStrings.AddMetadataField}
                        disabled={!canEditSelected}
                        onChange={(_, option) =>
                            updateField(section.key, index, { internalName: (option?.key as string) ?? "", value: "" })
                        }
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { minWidth: 0, maxWidth: 400 } }}>
                    {renderValueEditor(section.key, fieldValue, index, fieldDef)}
                </Stack.Item>
                {fieldWarning && (
                    <TooltipHost content={fieldWarning}>
                        <Icon iconName="Warning" className={warningIconStyle} style={{ marginTop: 8 }} />
                    </TooltipHost>
                )}
                <IconButton
                    iconProps={{ iconName: "Delete" }}
                    ariaLabel={languageStrings.Delete}
                    disabled={!canEditSelected}
                    onClick={() => removeField(section.key, index)}
                />
            </Stack>
        );
    };

    const renderTypeSection = (section: ITypeSection) => {
        const mapping = getMapping(section.key);
        const chosenLibrary = mapping?.libraryTitle
            ? section.libraries.find((l) => l.title.toLowerCase() === mapping.libraryTitle.toLowerCase())
            : undefined;
        const staleLibrary = !!mapping?.libraryTitle && !chosenLibrary;
        // A section is editable only when the design actually carries this type; a type known
        // solely from saved data keeps its values visible but cannot be authored against.
        const sectionEditable = canEditSelected && section.libraries.length > 0;
        return (
            <div key={section.key} className={typeSectionStyle}>
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                            <Label styles={{ root: { fontSize: 14, fontWeight: FontWeights.semibold, padding: 0 } }}>
                                {section.key}
                            </Label>
                            {staleLibrary && (
                                <TooltipHost content={languageStrings.NoLongerInSiteDesign}>
                                    <Icon iconName="Warning" className={warningIconStyle} />
                                </TooltipHost>
                            )}
                        </Stack>
                        {mapping?.libraryTitle && typeSections.length > 1 && (
                            <DefaultButton
                                iconProps={{ iconName: "Copy" }}
                                text={languageStrings.CopyToOtherTypes}
                                disabled={!canEditSelected}
                                onClick={() => setCopyConfirmSource(section.key)}
                            />
                        )}
                    </Stack>
                    <Stack.Item styles={{ root: { maxWidth: 400 } }}>
                        <Dropdown
                            label={languageStrings.DestinationLibrary}
                            options={buildLibraryOptions(section, mapping)}
                            selectedKey={mapping?.libraryTitle ?? ""}
                            disabled={isSelectedIgnored || (!sectionEditable && !mapping?.libraryTitle)}
                            onChange={(_, option) => onLibraryChange(section.key, option)}
                        />
                    </Stack.Item>
                    {mapping?.libraryTitle && (
                        <>
                            {mapping.fields.map((fieldValue, index) =>
                                renderFieldRow(section, mapping, chosenLibrary, fieldValue, index)
                            )}
                            <Stack.Item>
                                <DefaultButton
                                    iconProps={{ iconName: "Add" }}
                                    text={languageStrings.AddMetadataField}
                                    disabled={!sectionEditable || !chosenLibrary || mapping.fields.some((f) => !f.internalName)}
                                    onClick={() => addField(section.key)}
                                />
                            </Stack.Item>
                        </>
                    )}
                </Stack>
            </div>
        );
    };

    const renderRailRow = (row: IMappingRow) => {
        const mappedCount = typeSections.filter((section) => mappingByKey[mappingKey(row.key, section.key)]?.libraryTitle).length;
        const isSelected = selectedRow?.key === row.key;
        const isIgnored = ignoredKeys.has(row.key);
        return (
            <button
                key={row.key}
                type="button"
                className={isSelected ? `${railRowStyle} ${railRowSelectedStyle}` : railRowStyle}
                onClick={() => setSelectedKey(row.key)}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} styles={{ root: { minWidth: 0 } }}>
                    {row.isStale && (
                        <TooltipHost content={languageStrings.NoLongerInHix}>
                            <Icon iconName="Warning" className={warningIconStyle} />
                        </TooltipHost>
                    )}
                    <span className={railRowLabelStyle} title={row.label}>
                        {row.label}
                    </span>
                </Stack>
                <span className={!isIgnored && mappedCount > 0 ? mappedPillStyle : unmappedPillStyle}>
                    {isIgnored
                        ? languageStrings.Ignored
                        : mappedCount > 0
                            ? `${mappedCount}/${typeSections.length}`
                            : languageStrings.Unmapped}
                </span>
            </button>
        );
    };

    const siteDesignMessage =
        siteDesignStatus === "notConfigured"
            ? languageStrings.SiteDesignNotConfigured
            : siteDesignStatus === "tenantNotFound"
                ? languageStrings.TenantUnknownToProvisioning
                : siteDesignStatus === "unavailable"
                    ? languageStrings.SiteDesignUnavailable
                    : undefined;

    return (
        <Panel
            headerText={`${languageStrings.HixMappingsTitle} — ${tenantName}`}
            isOpen={isOpen}
            onDismiss={attemptClose}
            closeButtonAriaLabel={languageStrings.Close}
            type={PanelType.custom}
            customWidth={"calc(100vw - 208px)"}
            isLightDismiss={false}
        >
            {isLoading ? (
                <Spinner size={SpinnerSize.large} styles={{ root: { marginTop: 40 } }} />
            ) : (
                <>
                    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 10 } }}>
                        {siteDesignMessage && (
                            <MessageBar
                                messageBarType={
                                    siteDesignStatus === "unavailable" ? MessageBarType.error : MessageBarType.severeWarning
                                }
                            >
                                {siteDesignMessage}
                            </MessageBar>
                        )}
                        {mappingsLoadFailed && (
                            <MessageBar messageBarType={MessageBarType.error}>{languageStrings.MappingsLoadFailed}</MessageBar>
                        )}
                        {!hixConfigured && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {languageStrings.HixUploadTypesUnavailable}
                            </MessageBar>
                        )}
                        {saveFailed && (
                            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setSaveFailed(false)}>
                                {languageStrings.MappingsSaveFailed}
                            </MessageBar>
                        )}
                        {saveTooLarge && (
                            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setSaveTooLarge(false)}>
                                {languageStrings.MappingsTooLarge}
                            </MessageBar>
                        )}
                        {unreadableSaved > 0 && (
                            <MessageBar messageBarType={MessageBarType.warning}>{languageStrings.SavedMappingsPartlyUnreadable}</MessageBar>
                        )}
                        {neverConfigured && defaultsLoadFailed && (
                            <MessageBar messageBarType={MessageBarType.warning}>{languageStrings.DefaultsLoadFailed}</MessageBar>
                        )}
                        {autoFillDeferred && (
                            <MessageBar messageBarType={MessageBarType.info}>{languageStrings.DefaultsNotAutoFilled}</MessageBar>
                        )}
                        {defaultsNotice &&
                            (defaultsNotice.mode === "auto" ? (
                                defaultsNotice.mappingsAdded + defaultsNotice.ignoresAdded > 0 && (
                                    <MessageBar messageBarType={MessageBarType.info}>{languageStrings.DefaultsAutoFilledInfo}</MessageBar>
                                )
                            ) : (
                                <MessageBar
                                    messageBarType={
                                        defaultsNotice.mappingsAdded + defaultsNotice.ignoresAdded > 0
                                            ? MessageBarType.success
                                            : MessageBarType.info
                                    }
                                    onDismiss={() => setDefaultsNotice(null)}
                                >
                                    {/* "Nothing to add" only fits Fill: a Replace that applies nothing still cleared rows. */}
                                    {defaultsNotice.mode === "fill" && defaultsNotice.mappingsAdded + defaultsNotice.ignoresAdded === 0
                                        ? languageStrings.DefaultsNothingToAdd
                                        : formatString(languageStrings.DefaultsApplied, defaultsNotice.mappingsAdded, defaultsNotice.ignoresAdded)}
                                </MessageBar>
                            ))}
                        {defaultsNotice && defaultsNotice.skipped.length > 0 && (
                            <MessageBar
                                messageBarType={MessageBarType.warning}
                                onDismiss={() => setDefaultsNotice((notice) => (notice ? { ...notice, skipped: [] } : notice))}
                            >
                                {formatString(languageStrings.DefaultsSkippedSummary, defaultsNotice.skipped.length)}
                                {/* Lines wrap; past about six lines the list scrolls instead of pushing the editor down.
                                    Focusable and named, so it can be scrolled from the keyboard in every browser. */}
                                <ul
                                    tabIndex={0}
                                    aria-label={formatString(languageStrings.DefaultsSkippedSummary, defaultsNotice.skipped.length)}
                                    style={{ margin: "4px 0 0 0", paddingLeft: 18, maxHeight: 96, overflowY: "auto" }}
                                >
                                    {defaultsNotice.skipped.map((skip, index) => (
                                        <li key={index}>{describeSkip(skip, languageStrings)}</li>
                                    ))}
                                </ul>
                            </MessageBar>
                        )}
                        {defaultsSaveResult === "saved" && (
                            <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setDefaultsSaveResult(null)}>
                                {languageStrings.DefaultsSaved}
                            </MessageBar>
                        )}
                        {defaultsSaveResult === "failed" && (
                            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setDefaultsSaveResult(null)}>
                                {languageStrings.DefaultsSaveFailed}
                            </MessageBar>
                        )}
                        {defaultsSaveResult === "tooLarge" && (
                            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setDefaultsSaveResult(null)}>
                                {languageStrings.DefaultsTooLarge}
                            </MessageBar>
                        )}
                    </Stack>
                    <div className={layoutStyle}>
                        <div className={railStyle}>
                            <span className={railGroupHeaderStyle}>{languageStrings.StandardTaskTypes}</span>
                            {rows.filter((r) => r.kind === "task" && !r.isCustom).map(renderRailRow)}
                            {rows.some((r) => r.kind === "task" && r.isCustom) && (
                                <span className={railGroupHeaderStyle}>{languageStrings.CustomTaskTypes}</span>
                            )}
                            {rows.filter((r) => r.kind === "task" && r.isCustom).map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.DocumentTypes}</span>
                            {DOCUMENT_TYPE_GROUPS.map((group) => (
                                <React.Fragment key={group.headerKey}>
                                    <span className={railGroupHeaderStyle}>{languageStrings[group.headerKey]}</span>
                                    {group.types
                                        .map((t) => rows.find((r) => r.key === documentRowKey(t.key)))
                                        .filter((r): r is IMappingRow => !!r)
                                        .map(renderRailRow)}
                                </React.Fragment>
                            ))}
                            {rows
                                .filter((r) => r.kind === "document" && !DOCUMENT_TYPE_BY_ROW_KEY.has(r.key))
                                .map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.TemplateCategories}</span>
                            {rows.filter((r) => r.kind === "templateCategory").map(renderRailRow)}
                            <span className={railSectionHeaderStyle}>{languageStrings.QuestionCategories}</span>
                            {rows.filter((r) => r.kind === "questionCategory").map(renderRailRow)}
                        </div>
                        <div className={detailPaneStyle}>
                            {selectedRow && (
                                <Stack tokens={{ childrenGap: 14 }}>
                                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                                        <Stack styles={{ root: { minWidth: 0 } }}>
                                            <Label styles={{ root: { fontSize: 16, fontWeight: FontWeights.semibold } }}>
                                                {selectedRow.label}
                                            </Label>
                                            {selectedRow.kind === "document" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {documentTypeKeyFromRow(selectedRow.key)}
                                                    {selectedDocDef && ` — ${selectedDocDef.description}`}
                                                </span>
                                            )}
                                            {selectedRow.kind === "templateCategory" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {templateCategoryKeyFromRow(selectedRow.key)}
                                                </span>
                                            )}
                                            {selectedRow.kind === "questionCategory" && (
                                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                                    {questionCategoryKeyFromRow(selectedRow.key)}
                                                </span>
                                            )}
                                        </Stack>
                                        <Toggle
                                            label={
                                                selectedRow.kind === "document"
                                                    ? languageStrings.IgnoreDocumentType
                                                    : selectedRow.kind === "task"
                                                        ? languageStrings.IgnoreTaskType
                                                        : languageStrings.IgnoreCategory
                                            }
                                            inlineLabel
                                            checked={isSelectedIgnored}
                                            // canEdit, not canEditSelected — the toggle must stay
                                            // usable on an ignored type to un-ignore it.
                                            disabled={!canEdit}
                                            styles={{ root: { marginBottom: 0 } }}
                                            onChange={(_, checked) => toggleIgnored(selectedRow.key, checked === true)}
                                        />
                                    </Stack>
                                    {isSelectedIgnored && (
                                        <MessageBar messageBarType={MessageBarType.info}>
                                            {selectedRow.kind === "document"
                                                ? languageStrings.IgnoredDocumentTypeInfo
                                                : selectedRow.kind === "task"
                                                    ? languageStrings.IgnoredTaskTypeInfo
                                                    : languageStrings.IgnoredCategoryInfo}
                                        </MessageBar>
                                    )}
                                    {typeSections.map(renderTypeSection)}
                                </Stack>
                            )}
                        </div>
                    </div>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: { marginTop: 12 } }}>
                        <DefaultButton text={languageStrings.Defaults} iconProps={{ iconName: "Library" }} menuProps={{ items: defaultsMenuItems }} />
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton
                                text={languageStrings.Save}
                                disabled={!isDirty || !canEdit || isSaving}
                                onClick={onSave}
                            >
                                {isSaving && <Spinner size={SpinnerSize.small} style={{ marginLeft: 10 }} />}
                            </PrimaryButton>
                            <DefaultButton text={languageStrings.Cancel} onClick={attemptClose} />
                        </Stack>
                    </Stack>
                </>
            )}
            {copyConfirmSource && (() => {
                const preview = buildCopyPreview(copyConfirmSource);
                const anySettable = preview.some((p) => p.willSet);
                return (
                    <Dialog
                        hidden={false}
                        onDismiss={() => setCopyConfirmSource(null)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: languageStrings.CopyToOtherTypes,
                            closeButtonAriaLabel: languageStrings.Close,
                            subText: languageStrings.CopyConfirmIntro,
                        }}
                        modalProps={{ isBlocking: true, styles: { main: { maxWidth: 560 } } }}
                    >
                        <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginBottom: 6 } }}>
                            {preview.map((p) => (
                                <Stack key={p.typeKey} horizontal tokens={{ childrenGap: 8 }} verticalAlign="start">
                                    <Icon
                                        iconName={p.willSet ? "Accept" : "Blocked2"}
                                        className={p.willSet ? undefined : warningIconStyle}
                                        style={{ marginTop: 3, flexShrink: 0 }}
                                    />
                                    <span style={{ fontSize: 13 }}>
                                        <b>{p.typeKey}</b>: {p.text}
                                    </span>
                                </Stack>
                            ))}
                        </Stack>
                        <DialogFooter>
                            <PrimaryButton
                                text={languageStrings.Yes}
                                disabled={!anySettable}
                                onClick={() => {
                                    copyToOtherTypes(copyConfirmSource);
                                    setCopyConfirmSource(null);
                                }}
                            />
                            <DefaultButton text={languageStrings.Cancel} onClick={() => setCopyConfirmSource(null)} />
                        </DialogFooter>
                    </Dialog>
                );
            })()}
            {showReplaceConfirm && (
                <Dialog
                    hidden={false}
                    onDismiss={() => setShowReplaceConfirm(false)}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: languageStrings.ReplaceWithDefaults,
                        closeButtonAriaLabel: languageStrings.Close,
                        subText: languageStrings.ReplaceWithDefaultsConfirm,
                    }}
                    modalProps={{ isBlocking: true, styles: { main: { maxWidth: 480 } } }}
                >
                    <DialogFooter>
                        <PrimaryButton
                            text={languageStrings.Yes}
                            onClick={() => {
                                setShowReplaceConfirm(false);
                                applyDefaults("replace");
                            }}
                        />
                        <DefaultButton text={languageStrings.Cancel} onClick={() => setShowReplaceConfirm(false)} />
                    </DialogFooter>
                </Dialog>
            )}
            {showSaveDefaultsConfirm && (
                <Dialog
                    hidden={false}
                    onDismiss={() => setShowSaveDefaultsConfirm(false)}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: languageStrings.SaveAsDefaults,
                        closeButtonAriaLabel: languageStrings.Close,
                        subText: (() => {
                            // A failed load may hide an existing set, so it reads as a replacement.
                            const replacing = defaultsLoadFailed || !!defaultsInfo?.mappings;
                            const question = formatString(
                                replacing ? languageStrings.SaveAsDefaultsConfirm : languageStrings.SaveAsFirstDefaultsConfirm,
                                configurationName
                            );
                            return defaultsInfo?.updatedAtUtc
                                ? `${question} ${formatString(
                                    languageStrings.DefaultsLastSaved,
                                    defaultsInfo.updatedBy ?? "?",
                                    new Date(defaultsInfo.updatedAtUtc).toLocaleString()
                                )}`
                                : question;
                        })(),
                    }}
                    modalProps={{ isBlocking: true, styles: { main: { maxWidth: 480 } } }}
                >
                    <DialogFooter>
                        <PrimaryButton text={languageStrings.Yes} onClick={saveAsDefaults} />
                        <DefaultButton text={languageStrings.Cancel} onClick={() => setShowSaveDefaultsConfirm(false)} />
                    </DialogFooter>
                </Dialog>
            )}
            {showCloseConfirm && (
                <DialogMessage
                    isHidden={!showCloseConfirm}
                    hideDialog={() => setShowCloseConfirm(false)}
                    title={languageStrings.EditMappings}
                    message={languageStrings.UnsavedChangesLost}
                    confirmAction={() => {
                        setShowCloseConfirm(false);
                        onClose();
                    }}
                    cancelAction={() => setShowCloseConfirm(false)}
                    type={MessageType.YesNo}
                />
            )}
        </Panel>
    );
};

interface IHixMappingsPanelErrorBoundaryProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
}

/**
 * Keeps a render failure inside the panel: without it, React unmounts the whole portal and the
 * page goes blank. Shows a message in a panel that can still be closed.
 */
class HixMappingsPanelErrorBoundary extends React.Component<IHixMappingsPanelErrorBoundaryProps, { failed: boolean }> {
    static contextType = LanguageContext;

    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        console.error("HixMappingsPanel failed to render", error);
    }

    render() {
        if (!this.state.failed) {
            return this.props.children;
        }
        const strings = this.context as LanguageStrings;
        return (
            <Panel
                headerText={strings.HixMappingsTitle}
                isOpen={this.props.isOpen}
                onDismiss={this.props.onClose}
                closeButtonAriaLabel={strings.Close}
                type={PanelType.medium}
            >
                <MessageBar messageBarType={MessageBarType.error}>{strings.MappingsPanelError}</MessageBar>
            </Panel>
        );
    }
}

export const HixMappingsPanel: React.FunctionComponent<HixMappingsPanelProps> = (props) => (
    <HixMappingsPanelErrorBoundary isOpen={props.isOpen} onClose={props.onClose}>
        <HixMappingsPanelContent {...props} />
    </HixMappingsPanelErrorBoundary>
);

export default HixMappingsPanel;
